import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  makeStyles, tokens,
  Field, Input, Textarea, Checkbox, Button, Spinner,
  MessageBar, MessageBarBody, MessageBarActions,
} from '@fluentui/react-components';
import { API_BASE } from '../config.js';
import { Heading } from '../theme/primitives';
import { iconMap } from '../theme/icons';

const CloseIcon = iconMap.close;
const SendIcon = iconMap.send;

const useStyles = makeStyles({
  header: { marginBottom: tokens.spacingVerticalXXL },
  title: { marginBottom: tokens.spacingVerticalS },
  lead: { maxWidth: '32rem', margin: 0 },
  messageBar: { marginBottom: tokens.spacingVerticalL },
  form: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: tokens.spacingHorizontalL,
    '@media (min-width: 640px)': { gridTemplateColumns: '1fr 1fr' },
  },
  footerRow: {
    display: 'flex', flexDirection: 'column', alignItems: 'flex-start', justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM, paddingTop: tokens.spacingVerticalXS,
    '@media (min-width: 640px)': { flexDirection: 'row', alignItems: 'center' },
  },
  submitBtn: { flexShrink: 0 },
});

// Validuoja visus laukus vienu metu (ne "grąžina prie pirmos klaidos") —
// kiekviena klaida rodoma PRIE SAVO lauko per Field validationState/Message,
// ne viena bendra MessageBar juosta viršuje (žr. 40 punktų sąrašo site #4).
const validateContactForm = (form, t) => {
  const errors = {};
  if (!form.vardas.trim()) errors.vardas = t('contact_form.err_name');
  if (!form.pavarde.trim()) errors.pavarde = t('contact_form.err_surname');
  if (!/\S+@\S+\.\S+/.test(form.email)) errors.email = t('contact_form.err_email');
  if (!form.tel.trim()) errors.tel = t('contact_form.err_phone');
  if (!form.aprasymas.trim()) errors.aprasymas = t('contact_form.err_description');
  return errors;
};

function ContactForm({ hideHeader = false }) {
  const s = useStyles();
  const { t } = useTranslation();
  const [form, setForm] = useState({ vardas: '', pavarde: '', email: '', tel: '', aprasymas: '', sutinku: false });
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setFieldErrors(prev => (prev[name] ? { ...prev, [name]: undefined } : prev));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateContactForm(form, t);
    if (Object.keys(errors).length > 0) { setFieldErrors(errors); return; }
    setFieldErrors({});
    setSending(true); setResult(null);
    try {
      const res = await fetch(`${API_BASE}/contact-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || t('contact_form.generic_error')); }
      setResult({ type: 'success', msg: t('contact_form.success') });
      setForm({ vardas: '', pavarde: '', email: '', tel: '', aprasymas: '', sutinku: false });
    } catch (err) { setResult({ type: 'error', msg: err.message }); }
    finally { setSending(false); }
  };

  return (
    <div>
      {!hideHeader && (
        <div className={s.header}>
          <Heading variant="h1" className={s.title}>{t('send_request.title')}</Heading>
          <Heading variant="bodyLarge" className={s.lead}>
            {t('contact_form.lead')}
          </Heading>
        </div>
      )}

      {result && (
        <MessageBar intent={result.type === 'success' ? 'success' : 'error'} className={s.messageBar}>
          <MessageBarBody>{result.msg}</MessageBarBody>
          <MessageBarActions
            containerAction={
              <Button
                appearance="transparent"
                aria-label={t('topbar.close')}
                icon={<CloseIcon />}
                onClick={() => setResult(null)}
              />
            }
          />
        </MessageBar>
      )}

      <form onSubmit={handleSubmit} className={s.form}>
        <div className={s.grid}>
          <Field label={t('contact_form.name')} validationState={fieldErrors.vardas ? 'error' : undefined} validationMessage={fieldErrors.vardas}>
            <Input name="vardas" value={form.vardas} onChange={handleChange} placeholder="Jonas" required />
          </Field>
          <Field label={t('contact_form.surname')} validationState={fieldErrors.pavarde ? 'error' : undefined} validationMessage={fieldErrors.pavarde}>
            <Input name="pavarde" value={form.pavarde} onChange={handleChange} placeholder="Jonaitis" required />
          </Field>
          <Field label={t('contact_form.email')} validationState={fieldErrors.email ? 'error' : undefined} validationMessage={fieldErrors.email}>
            <Input name="email" type="email" value={form.email} onChange={handleChange} placeholder="jonas@pvz.lt" required />
          </Field>
          <Field label={t('contact_form.phone')} validationState={fieldErrors.tel ? 'error' : undefined} validationMessage={fieldErrors.tel}>
            <Input name="tel" value={form.tel} onChange={handleChange} placeholder="+370 600 00000" required />
          </Field>
        </div>

        <Field label={t('contact_form.description')} validationState={fieldErrors.aprasymas ? 'error' : undefined} validationMessage={fieldErrors.aprasymas}>
          <Textarea
            name="aprasymas"
            value={form.aprasymas}
            onChange={handleChange}
            placeholder={t('contact_form.description_ph')}
            rows={6}
            resize="none"
            required
          />
        </Field>

        <div className={s.footerRow}>
          <Checkbox
            name="sutinku"
            checked={form.sutinku}
            onChange={handleChange}
            label={t('contact_form.wants_consultation')}
          />
          <Button
            type="submit"
            appearance="primary"
            size="large"
            disabled={sending}
            icon={sending ? <Spinner size="tiny" /> : <SendIcon />}
            className={s.submitBtn}
          >
            {t('contact_form.submit')}
          </Button>
        </div>
      </form>
    </div>
  );
}

export default ContactForm;
